import { defineProcedure, ProcedureCategory } from '../../../../ontology.model';

import { models } from './representations';

// ------------------------------------------------
// TYPE-SAFE PROCEDURES
// ------------------------------------------------
// Each procedure is type-checked against the model registry.
// TypeScript will error if inputs/outputs don't match.

export const proc = {
  calcNth: defineProcedure(models, {
    id: 'proc.geo.calc_nth',
    name: 'Calculate N-th Term',
    category: ProcedureCategory.Calculation,
    input_ids: ['rep.geo.params'],
    output_id: 'rep.geo.term_result',
    formula_latex: 'u_n = a \\cdot r^{n-1}',
    dependencies: [],

    execute: (p) => ({
      value: p.a * Math.pow(p.r, p.n - 1),
      notation: `u_{${p.n}}`,
      expression: `${p.a}(${p.r})^{${p.n - 1}}`,
    }),
  }),

  calcPartialSum: defineProcedure(models, {
    id: 'proc.geo.calc_partial_sum',
    name: 'Calculate Partial Sum',
    category: ProcedureCategory.Calculation,
    input_ids: ['rep.geo.params'],
    output_id: 'rep.geo.finite_sum_result',
    formula_latex: 'S_n = a \\frac{1-r^n}{1-r}',
    dependencies: [],

    execute: (p) => {
      if (Math.abs(p.r - 1) < Number.EPSILON) {
        return {
          value: p.n * p.a,
          notation: `S_{${p.n}}`,
          expression: `${p.n} \\cdot ${p.a}`,
        };
      }

      const numerator = 1 - Math.pow(p.r, p.n);
      const denominator = 1 - p.r;
      const value = p.a * (numerator / denominator);

      return {
        value,
        notation: `S_{${p.n}}`,
        expression: `${p.a} \\frac{1 - (${p.r})^{${p.n}}}{1 - ${p.r}}`,
      };
    },
  }),

  determineMonotonicity: defineProcedure(models, {
    id: 'proc.geo.determine_monotonicity',
    name: 'Determine Monotonicity',
    category: ProcedureCategory.Classifier,
    input_ids: ['rep.geo.params'],
    output_id: 'rep.geo.behavior',
    formula_latex: '|r|<1 \\to Dec, r>1 \\to Inc, r<0 \\to Alt',
    dependencies: [],

    execute: (p) => {
      if (p.r < 0) {
        return { state: 'ALTERNATING', description: 'Signs flip (+/-).' };
      }
      if (p.r > 1) {
        return { state: 'INCREASING', description: 'Diverges to infinity.' };
      }
      if (p.r > 0 && p.r < 1) {
        return { state: 'DECREASING', description: 'Converges to zero.' };
      }
      return { state: 'CONSTANT', description: 'Terms are static.' };
    },
  }),

  determineConvergence: defineProcedure(models, {
    id: 'proc.geo.determine_convergence',
    name: 'Determine Convergence',
    category: ProcedureCategory.Classifier,
    input_ids: ['rep.geo.params'],
    output_id: 'rep.geo.convergence',
    formula_latex: '|r| < 1',
    dependencies: [],

    execute: (p) => ({
      status: Math.abs(p.r) < 1 ? 'CONVERGENT' : 'DIVERGENT',
    }),
  }),

  calcInfiniteSum: defineProcedure(models, {
    id: 'proc.geo.calc_infinite_sum',
    name: 'Calculate Infinite Sum',
    category: ProcedureCategory.Calculation,
    input_ids: ['rep.geo.params', 'rep.geo.convergence'],
    output_id: 'rep.geo.sum_result',
    formula_latex: 'S_{\\infty} = \\frac{a}{1-r}',
    dependencies: [],

    execute: (params, convergence) => {
      if (convergence.status === 'DIVERGENT') {
        return {
          value: NaN,
          notation: 'S_{∞}',
          expression: 'Undefined (divergent)',
        };
      }

      return {
        value: params.a / (1 - params.r),
        notation: 'S_{∞}',
        expression: `\\frac{${params.a}}{1 - ${params.r}}`,
      };
    },
  }),
};

// Export as array for the compiler
export const geometricProcedures = Object.values(proc);
